import { Box, Strong } from '@customerio/pluma-components/react';
import { DataSourceLabel } from '@/components/data-source-label';
import { isEmptyString } from '@/utils/string';
import {
  displayValue,
  operatorLabel,
  operatorShape,
  relativeDays,
  type AttributeConditionReading,
} from '../utils/segment-conditions';
import * as styles from './attribute-condition.css';

type ConditionValueProps = {
  operator: string;
  value: string | null;
};

/**
 * One value, as a chip — or `""` for an empty one, which is how Ember's
 * viewer writes a value that is not there.
 *
 * It is subscriber data, unlike the attribute's name, so it masks itself out
 * of FullStory session replay: the list can render inside a popover that
 * portals to the body, beyond any ancestor's mask (FPS-3731).
 */
function ConditionValue({ operator, value }: ConditionValueProps) {
  const shown = displayValue(operator, value);
  // A blank value is still a value to Ember, whose chip then reads as empty;
  // the label here would call it missing instead, so it takes the empty
  // value's words rather than a dimmed chip.
  if (shown == null || isEmptyString(shown)) {
    return <span>""</span>;
  }
  return <DataSourceLabel type="value" name={shown} variant="bordered-subtle" className="fs-mask" />;
}

type ConditionValuesProps = {
  condition: AttributeConditionReading;
};

/** Whatever the operator asks of a value: nothing, one, two, or a count of days. */
function ConditionValues({ condition }: ConditionValuesProps) {
  const { operator } = condition;
  const shape = operatorShape(operator);
  switch (shape) {
    case 'none':
      return null;
    case 'one':
      return <ConditionValue operator={operator} value={condition.value} />;
    case 'two':
      return (
        <>
          <ConditionValue operator={operator} value={condition.value} />
          <span>and</span>
          <ConditionValue operator={operator} value={condition.value2} />
        </>
      );
    case 'relative-days': {
      const { days, unit, direction } = relativeDays(condition.value);
      return (
        <>
          <Strong>{days}</Strong>
          <span>{unit}</span>
          <Strong>{direction}</Strong>
        </>
      );
    }
    default: {
      const _exhaustive: never = shape;
      return String(_exhaustive);
    }
  }
}

type AttributeConditionProps = {
  condition: AttributeConditionReading;
};

/**
 * An attribute condition as a segment's Usage column draws it in Ember
 * (`cio-condition-viewer-attribute`): the attribute as a chip, the operator in
 * words, then its values as chips, left-aligned in a bordered box.
 */
export function AttributeCondition({ condition }: AttributeConditionProps) {
  return (
    <Box className={styles.condition}>
      <Box className={styles.sentence}>
        <DataSourceLabel type="attribute" name={condition.name} variant="bordered-subtle" />
        <span>{operatorLabel(condition.operator)}</span>
        <ConditionValues condition={condition} />
      </Box>
    </Box>
  );
}
